import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  BarChart3, Eye, MousePointer, Play, Download, Calendar,
  Filter, RefreshCw, MapPin, Film, Target, FileSpreadsheet, 
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function PlayerReportsPage() {
  const [reports, setReports] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Detailed reports state
  const [detailedReports, setDetailedReports] = useState([]);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [detailedPagination, setDetailedPagination] = useState({ page: 1, page_size: 50, total: 0, total_pages: 0 });
  
  const [filters, setFilters] = useState({
    start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    campaign_id: '',
    creative_id: '',
    screen_name: ''
  });

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.creative_id) params.append('creative_id', filters.creative_id);
      if (filters.screen_name) params.append('screen_name', filters.screen_name);
      
      const res = await fetch(`${API_URL}/api/player/reports?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data);
      }
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/player-campaigns`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns');
    }
  }, []);

  const fetchCreatives = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/creatives`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCreatives(data);
      }
    } catch (error) {
      console.error('Failed to fetch creatives');
    }
  }, []);

  const fetchDetailedReports = useCallback(async (page = 1) => {
    setDetailedLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.creative_id) params.append('creative_id', filters.creative_id);
      params.append('page', page);
      params.append('page_size', 50);
      
      const res = await fetch(`${API_URL}/api/player/reports/detailed?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDetailedReports(data.reports);
        setDetailedPagination(data.pagination);
      }
    } catch (error) {
      toast.error('Failed to fetch detailed reports');
    } finally {
      setDetailedLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchReports();
    fetchCampaigns();
    fetchCreatives();
  }, [fetchReports, fetchCampaigns, fetchCreatives]);

  const handleExportDetailedReports = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      if (filters.creative_id) params.append('creative_id', filters.creative_id);
      
      const res = await fetch(`${API_URL}/api/player/reports/detailed/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `detailed_report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Report exported successfully');
      } else {
        toast.error('Failed to export report');
      }
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.campaign_id) params.append('campaign_id', filters.campaign_id);
      
      const res = await fetch(`${API_URL}/api/player/reports/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `player_report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Report exported');
      } else {
        toast.error('Failed to export report');
      }
    } catch (error) {
      toast.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const getCampaignName = (id) => {
    return campaigns.find(c => c.id === id)?.name || id?.slice(0, 8) || 'Unknown';
  };

  const getCreativeName = (id) => {
    return creatives.find(c => c.id === id)?.name || id?.slice(0, 8) || 'Unknown';
  };

  // Video funnel data
  const funnelData = reports ? [
    { name: 'Impressions', value: reports.total_impressions, fill: '#3b82f6' },
    { name: '25%', value: reports.total_quartile_25, fill: '#10b981' },
    { name: '50%', value: reports.total_quartile_50, fill: '#f59e0b' },
    { name: '75%', value: reports.total_quartile_75, fill: '#ef4444' },
    { name: 'Complete', value: reports.total_complete, fill: '#8b5cf6' },
    { name: 'Clicks', value: reports.total_clicks, fill: '#ec4899' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Player Reports</h1>
          <p className="text-zinc-400 mt-1">Track impressions, clicks, and video engagement</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReports} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleExport} disabled={exporting} className="bg-blue-600 hover:bg-blue-700">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="glass-card border-zinc-800">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label className="text-zinc-400">Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                className="bg-zinc-900 border-zinc-700 w-40"
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                className="bg-zinc-900 border-zinc-700 w-40"
                data-testid="filter-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Campaign</Label>
              <Select value={filters.campaign_id} onValueChange={(v) => setFilters({ ...filters, campaign_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-48 bg-zinc-900 border-zinc-700" data-testid="filter-campaign">
                  <SelectValue placeholder="All Campaigns" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Creative</Label>
              <Select value={filters.creative_id} onValueChange={(v) => setFilters({ ...filters, creative_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="w-48 bg-zinc-900 border-zinc-700" data-testid="filter-creative">
                  <SelectValue placeholder="All Creatives" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-700">
                  <SelectItem value="all">All Creatives</SelectItem>
                  {creatives.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">Screen Name</Label>
              <Input
                value={filters.screen_name}
                onChange={(e) => setFilters({ ...filters, screen_name: e.target.value })}
                placeholder="Filter by screen..."
                className="bg-zinc-900 border-zinc-700 w-40"
                data-testid="filter-screen"
              />
            </div>
            <Button onClick={fetchReports} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-filters-btn">
              <Filter className="w-4 h-4 mr-2" />
              Apply
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      ) : reports ? (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Impressions</p>
                    <p className="text-2xl font-bold text-white" data-testid="total-impressions">{formatNumber(reports.total_impressions)}</p>
                  </div>
                  <Eye className="w-8 h-8 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Clicks</p>
                    <p className="text-2xl font-bold text-white" data-testid="total-clicks">{formatNumber(reports.total_clicks)}</p>
                  </div>
                  <MousePointer className="w-8 h-8 text-emerald-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">CTR</p>
                    <p className="text-2xl font-bold text-white" data-testid="total-ctr">{reports.ctr}%</p>
                  </div>
                  <Target className="w-8 h-8 text-amber-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">25% Viewed</p>
                    <p className="text-2xl font-bold text-white">{formatNumber(reports.total_quartile_25)}</p>
                  </div>
                  <Play className="w-8 h-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">Completed</p>
                    <p className="text-2xl font-bold text-white">{formatNumber(reports.total_complete)}</p>
                  </div>
                  <Film className="w-8 h-8 text-rose-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card className="glass-card border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-zinc-400 text-sm">VTR</p>
                    <p className="text-2xl font-bold text-white">{reports.vtr}%</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-cyan-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="bg-zinc-800">
              <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600">Overview</TabsTrigger>
              <TabsTrigger value="detailed" className="data-[state=active]:bg-blue-600" onClick={() => fetchDetailedReports(1)} data-testid="detailed-reports-tab">
                <FileSpreadsheet className="w-4 h-4 mr-1" />
                Detailed Reports
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-blue-600">By Campaign</TabsTrigger>
              <TabsTrigger value="creatives" className="data-[state=active]:bg-blue-600">By Creative</TabsTrigger>
              <TabsTrigger value="screens" className="data-[state=active]:bg-blue-600">By Screen</TabsTrigger>
              <TabsTrigger value="geo" className="data-[state=active]:bg-blue-600">By Location</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Daily Trend */}
                <Card className="glass-card border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Daily Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={reports.by_time}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis dataKey="date" stroke="#71717a" tick={{ fill: '#71717a' }} />
                          <YAxis stroke="#71717a" tick={{ fill: '#71717a' }} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                            labelStyle={{ color: '#fff' }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="impressions" stroke="#3b82f6" strokeWidth={2} name="Impressions" />
                          <Line type="monotone" dataKey="clicks" stroke="#10b981" strokeWidth={2} name="Clicks" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Video Funnel */}
                <Card className="glass-card border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white">Video Engagement Funnel</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={funnelData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                          <XAxis type="number" stroke="#71717a" tick={{ fill: '#71717a' }} />
                          <YAxis dataKey="name" type="category" stroke="#71717a" tick={{ fill: '#71717a' }} width={80} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                            labelStyle={{ color: '#fff' }}
                          />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {funnelData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Detailed Reports Tab */}
            <TabsContent value="detailed" className="space-y-4">
              {/* Export Button */}
              <div className="flex justify-end">
                <Button onClick={handleExportDetailedReports} disabled={exporting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-detailed-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download Report
                </Button>
              </div>

              {/* Detailed Reports Table */}
              <Card className="glass-card border-zinc-800">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-zinc-500 whitespace-nowrap">Date</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap">Campaign ID</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap">Campaign Name</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap">Creative ID</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap">Creative Name</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">Impressions</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">Clicks</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">First Quartile</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">Second Quartile</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">Third Quartile</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">Complete</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap">Screen Name</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap">Device ID</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">Lat</TableHead>
                        <TableHead className="text-zinc-500 whitespace-nowrap text-right">Long</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detailedLoading ? (
                        <TableRow>
                          <TableCell colSpan={15} className="text-center py-8 text-zinc-500">
                            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                            Loading reports...
                          </TableCell>
                        </TableRow>
                      ) : detailedReports.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={15} className="text-center py-8 text-zinc-500">
                            No reports found for selected filters
                          </TableCell>
                        </TableRow>
                      ) : (
                        detailedReports.map((report, idx) => (
                          <TableRow key={idx} className="border-zinc-800" data-testid={`report-row-${idx}`}>
                            <TableCell className="whitespace-nowrap text-zinc-300">{report.date}</TableCell>
                            <TableCell className="whitespace-nowrap text-zinc-400 font-mono text-xs">{report.campaign_id?.slice(0, 8) || '-'}</TableCell>
                            <TableCell className="whitespace-nowrap text-white">{report.campaign_name}</TableCell>
                            <TableCell className="whitespace-nowrap text-zinc-400 font-mono text-xs">{report.creative_id?.slice(0, 8) || '-'}</TableCell>
                            <TableCell className="whitespace-nowrap text-white">{report.creative_name}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-blue-400 font-medium">{report.impressions}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-emerald-400 font-medium">{report.clicks}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-amber-400">{report.first_quartile}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-orange-400">{report.second_quartile}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-purple-400">{report.third_quartile}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-rose-400">{report.complete}</TableCell>
                            <TableCell className="whitespace-nowrap text-zinc-300">{report.screen_name || '-'}</TableCell>
                            <TableCell className="whitespace-nowrap text-cyan-400 font-mono text-xs">{report.device_id || '-'}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-zinc-400 font-mono text-sm">{report.latitude?.toFixed(4) || '-'}</TableCell>
                            <TableCell className="whitespace-nowrap text-right text-zinc-400 font-mono text-sm">{report.longitude?.toFixed(4) || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {detailedPagination.total_pages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t border-zinc-800">
                    <div className="text-zinc-400 text-sm">
                      Showing {((detailedPagination.page - 1) * detailedPagination.page_size) + 1} - {Math.min(detailedPagination.page * detailedPagination.page_size, detailedPagination.total)} of {detailedPagination.total} records
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchDetailedReports(detailedPagination.page - 1)}
                        disabled={detailedPagination.page <= 1 || detailedLoading}
                        className="border-zinc-700"
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <span className="text-zinc-400 px-3">
                        Page {detailedPagination.page} of {detailedPagination.total_pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchDetailedReports(detailedPagination.page + 1)}
                        disabled={detailedPagination.page >= detailedPagination.total_pages || detailedLoading}
                        className="border-zinc-700"
                        data-testid="next-page-btn"
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </TabsContent>

            {/* Campaigns Tab */}
            <TabsContent value="campaigns">
              <Card className="glass-card border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Campaign</TableHead>
                      <TableHead className="text-zinc-500 text-right">Impressions</TableHead>
                      <TableHead className="text-zinc-500 text-right">Clicks</TableHead>
                      <TableHead className="text-zinc-500 text-right">Completed</TableHead>
                      <TableHead className="text-zinc-500 text-right">CTR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.by_campaign.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                          No data for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.by_campaign.map((row, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell className="font-medium text-white">{getCampaignName(row.campaign_id)}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.impressions)}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.clicks)}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.complete)}</TableCell>
                          <TableCell className="text-right text-zinc-300">
                            {row.impressions > 0 ? ((row.clicks / row.impressions) * 100).toFixed(2) : '0.00'}%
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Creatives Tab */}
            <TabsContent value="creatives">
              <Card className="glass-card border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Creative</TableHead>
                      <TableHead className="text-zinc-500 text-right">Impressions</TableHead>
                      <TableHead className="text-zinc-500 text-right">Clicks</TableHead>
                      <TableHead className="text-zinc-500 text-right">Completed</TableHead>
                      <TableHead className="text-zinc-500 text-right">CTR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.by_creative.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-zinc-500">
                          No data for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.by_creative.map((row, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell className="font-medium text-white">{getCreativeName(row.creative_id)}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.impressions)}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.clicks)}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.complete)}</TableCell>
                          <TableCell className="text-right text-zinc-300">
                            {row.impressions > 0 ? ((row.clicks / row.impressions) * 100).toFixed(2) : '0.00'}%
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Screens Tab */}
            <TabsContent value="screens">
              <Card className="glass-card border-zinc-800">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-500">Screen Name</TableHead>
                      <TableHead className="text-zinc-500 text-right">Impressions</TableHead>
                      <TableHead className="text-zinc-500 text-right">Clicks</TableHead>
                      <TableHead className="text-zinc-500 text-right">CTR</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reports.by_screen.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-zinc-500">
                          No data for selected period
                        </TableCell>
                      </TableRow>
                    ) : (
                      reports.by_screen.map((row, idx) => (
                        <TableRow key={idx} className="border-zinc-800">
                          <TableCell className="font-medium text-white">{row.screen_name}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.impressions)}</TableCell>
                          <TableCell className="text-right text-zinc-300">{formatNumber(row.clicks)}</TableCell>
                          <TableCell className="text-right text-zinc-300">
                            {row.impressions > 0 ? ((row.clicks / row.impressions) * 100).toFixed(2) : '0.00'}%
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Geo Tab */}
            <TabsContent value="geo">
              <Card className="glass-card border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Geographic Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reports.by_geo.length === 0 ? (
                    <div className="text-center py-12 text-zinc-500">
                      No location data available
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {reports.by_geo.slice(0, 12).map((geo, idx) => (
                        <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-zinc-400 text-sm">
                              {geo.lat.toFixed(2)}, {geo.lng.toFixed(2)}
                            </span>
                            <Badge className="bg-blue-500/20 text-blue-400">
                              {formatNumber(geo.count)} events
                            </Badge>
                          </div>
                          <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-blue-500 rounded-full"
                              style={{ width: `${(geo.count / Math.max(...reports.by_geo.map(g => g.count))) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      ) : (
        <div className="text-center py-12 text-zinc-500">
          No data available. Create campaigns and start serving ads!
        </div>
      )}
    </div>
  );
}
